import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./page.module.css";
import DateRangeButtons from "@/components/DateRangeSelector/DateRangeButtons";
import { useAsinAnalytics } from "@/contexts/AsinAnalyticsContext";
import { useLocation } from "wouter";
import Card, { TotalCard } from "@/components/Analytics/Card";
import { Table, TableColumn } from "@/components/Table/Table";
import { AsinProfitDetails, ProfitabilityByAsinResponseBody } from "@boxem/core-api-schema";
import Skeleton from "@/components/Skeleton/Skeleton";
import TextInput from "@/components/forms/TextInput/TextInput";
import { formatValue } from "@/utils/formatUtils";
import Pagination from "@/components/Pagination/Pagination";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import IconSearch from "@/components/icons/IconSearch";
import PageContainer from "@/app/settings/container/PageContainer";
import { DateRangeValue, startOfToday, endOfToday } from "@/utils/dateUtils";
import { getLocalStorageItem } from "@/utils/localStorageUtils";
import { useCache } from "@/hooks/cacheHooks/useCache";
import { computeCacheKey, PaginatedData } from "../profit-by-sku/page";
import { getAsinProfitability, getAsinProfitabilityBreakDown } from "@/api/seller-analytics";

// Path generator for Table rows/cells
const getAsinDetailPathForRow = (asin: string): string => {
    if (!asin) return '#';
    return `/profit-by-asin/${encodeURIComponent(asin)}?analytics=true`;
};

// Path generator specifically for Cards
const getAsinDetailPathForCard = (asin: string): string => {
    if (!asin) return '#';
    return `/profit-by-asin/${encodeURIComponent(asin)}?analytics=true`;
};


const ProfitByAsinPage = () => {

    const [_, navigate] = useLocation();
    const tableContainerRef = useRef<HTMLDivElement>(null);

    const [timeFrame, setTimeFrame] = useState<DateRangeValue>(
        getLocalStorageItem('analyticsTimeFrame', {
          from: startOfToday.startOf("month").startOf('day').toDate(),
          to: endOfToday.toDate(),
          label: 'This month'
        })
    );

    const [page, setPage] = useState<number>(1);
    const [limit, setLimitLocal] = useState<number>(10);
    const [search, setSearchLocal] = useState<string>('');
    const [sortedColumnId, setSortedColumnId] = useState<string | undefined>(undefined);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | undefined>(undefined);

    const setSearch = useCallback((newSearch: string) => {
        setPage(1); // Reset to first page
        setSearchLocal(newSearch);
      }, []);
    
    const setLimit = useCallback((newLimit: number) => {
        setPage(1); // Reset to first page
        setLimitLocal(newLimit);
    }, []);

    const setSort = useCallback((id: string, direction: 'asc' | 'desc') => {
        console.log('sort change', id, direction);
        setSortedColumnId(id);
        setSortDirection(direction);
        setPage(1);
    }, []);

    const { data: overviewData, isLoading: isOverviewLoading } = useCache(computeCacheKey(timeFrame, "/seller-analytics/profit-by-asin/overview"), () => getAsinProfitabilityBreakDown({ interval: timeFrame }));
    const { data: profitabilityData, isLoading: isProfitabilityLoading } = useCache<ProfitabilityByAsinResponseBody>(computeCacheKey(timeFrame, "/seller-analytics/profit-by-asin"), () => getAsinProfitability({ interval: timeFrame }));


    const filteredAndPaginatedData = useMemo<PaginatedData<AsinProfitDetails>>(() => {
        if (!profitabilityData) return null;

        let filtered = profitabilityData.asins.filter(item => {
            const searchLower = search.toLowerCase();
            return (
                item.asin.toLowerCase().includes(searchLower) ||
                item.name.toLowerCase().includes(searchLower)
            );
        });

        if (sortedColumnId && sortDirection) {
            filtered = [...filtered].sort((a, b) => {
                const aValue = a[sortedColumnId];
                const bValue = b[sortedColumnId];

                if (typeof aValue === 'number' && typeof bValue === 'number') {
                    return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
                }

                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    return sortDirection === 'asc'
                        ? aValue.localeCompare(bValue)
                        : bValue.localeCompare(aValue);
                }

                return 0;
            });
        }

        const totalItems = filtered.length;
        const totalPages = Math.ceil(totalItems / limit);
        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        const paginatedItems = filtered.slice(startIndex, endIndex);

        return {
            items: paginatedItems,
            total: totalItems,
            page,
            limit,
            totalPages
        };
    }, [profitabilityData, search, page, limit, sortedColumnId, sortDirection]);

    const totalPages = useMemo(() => {
        if (!filteredAndPaginatedData) return 0;
        return Math.ceil(filteredAndPaginatedData.total / limit);
    }, [filteredAndPaginatedData, limit]);


    const cardAnalytics = useMemo(() => {
        return ([
            {
                asin: overviewData?.highestProfit?.asin,
                label: "Highest Profit",
                value: formatValue((overviewData?.highestProfit?.total ?? 0), "currency"),
                name: overviewData?.highestProfit?.productName,
                image: overviewData?.highestProfit?.image,
                href: overviewData?.highestProfit?.asin ? getAsinDetailPathForCard(overviewData.highestProfit.asin) : undefined,
            },
            {
                asin: overviewData?.highestRevenue?.asin,
                label: "Highest Revenue",
                value: formatValue((overviewData?.highestRevenue?.total ?? 0), "currency"),
                name: overviewData?.highestRevenue?.productName,
                image: overviewData?.highestRevenue?.image,
                href: overviewData?.highestRevenue?.asin ? getAsinDetailPathForCard(overviewData.highestRevenue.asin) : undefined,
            },
            {
                asin: overviewData?.highestUnitsSold?.asin,
                label: "Highest Units Sold",
                value: formatValue((overviewData?.highestUnitsSold?.total ?? 0), "number"),
                name: overviewData?.highestUnitsSold?.productName,
                image: overviewData?.highestUnitsSold?.image,
                href: overviewData?.highestUnitsSold?.asin ? getAsinDetailPathForCard(overviewData.highestUnitsSold.asin) : undefined,
            },
            {
                asin: overviewData?.highestRefunds?.asin,
                label: "Most Refunds",
                value: formatValue((overviewData?.highestRefunds?.total ?? 0), "number"),
                name: overviewData?.highestRefunds?.productName,
                image: overviewData?.highestRefunds?.image,
                href: overviewData?.highestRefunds?.asin ? getAsinDetailPathForCard(overviewData.highestRefunds.asin) : undefined,
            },
            {
                label: "Amount of Asins",
                value: formatValue((overviewData?.totalASINs ?? 0), "number"),
                subHeading: "Total",
                asin: undefined, href: undefined, name: undefined, image: undefined,
            }
        ]);
    }, [overviewData]);

    const columns: TableColumn<AsinProfitDetails>[] = useMemo(() => {
        const createNavigationHandler = (row) => {
            const asin = row.getCellData('asin');
            if (asin) {
                const path = getAsinDetailPathForRow(asin);
                return () => navigate(path);
            }
            return undefined;
        };

        return [
            {
                title: 'Image',
                id: 'image',
                dataKey: 'image',
                sortable: false,
                pinned: 'left',
                width: '4rem',
                renderRowCell: (row) => {
                    const handleNavigate = createNavigationHandler(row);
                    const asin = row.getCellData('asin');
                    return (
                        <button
                            className={`${styles.button} ${styles.imageButton}`}
                            onClick={handleNavigate}
                            disabled={!asin}
                            aria-label={`View details for ${row.getCellData('name')}`}
                        >
                           {row.getCellData('image') ?
                                <img src={row.getCellData('image')} alt={row.getCellData('name')} className={styles.image}/>
                           : <div className={styles.image}></div>}
                        </button>
                    );
                },
            },
            {
                title: 'Product',
                id: 'name',
                dataKey: 'name',
                sortable: true,
                width: '19rem',
                renderRowCell: (row) => {
                     const handleNavigate = createNavigationHandler(row);
                     const asin = row.getCellData('asin');
                     return (
                         <button className={styles.button} onClick={handleNavigate} disabled={!asin}>
                            <span>{row.getCellData('name')}</span>
                        </button>
                     );
                },
                renderSkeleton: () => (
                    <div className={styles.rowCell}><Skeleton width={'auto'}/></div>
                )
            },
            {
                title: 'ASIN',
                id: 'asin',
                dataKey: 'asin',
                sortable: true,
                width: '7.5rem',
                renderRowCell: (row) => {
                    const handleNavigate = createNavigationHandler(row);
                    const asin = row.getCellData('asin');
                    return (
                        <button className={`${styles.button} ${styles.rightAlign}`} onClick={handleNavigate} disabled={!asin}>
                            <span>{row.getCellData('asin')}</span>
                        </button>
                    );
                },
                renderHeaderCell: () => (<span className={styles.headerCell}>ASIN</span>)
            },
            {
                title: 'List Price',
                id: 'listPrice',
                dataKey: 'listPrice',
                width: '7.5rem',
                sortable: true,
                renderRowCell: (row) => {
                    const handleNavigate = createNavigationHandler(row);
                    const asin = row.getCellData('asin');
                    return (
                         <button className={`${styles.button} ${styles.rightAlign}`} onClick={handleNavigate} disabled={!asin}>
                            <span>{formatValue(row.getCellData('listPrice'), 'currency')}</span>
                        </button>
                    );
                }
            },
            {
                title: 'Buy Cost',
                id: 'avgBuyCost',
                dataKey: 'avgBuyCost',
                sortable: true,
                width: '7.5rem',
                renderRowCell: (row) => {
                     const handleNavigate = createNavigationHandler(row);
                     const asin = row.getCellData('asin');
                     return (
                         <button className={`${styles.button} ${styles.rightAlign}`} onClick={handleNavigate} disabled={!asin}>
                            <span>{formatValue(row.getCellData('avgBuyCost'), "currency")}</span>
                        </button>
                     );
                }
            },
            {
                title: 'In Stock Qty',
                id: 'inStockQty',
                dataKey: 'inStockQty',
                width: '7.5rem',
                sortable: true,
                renderRowCell: (row) => {
                    const handleNavigate = createNavigationHandler(row);
                    const asin = row.getCellData('asin');
                    return (
                        <button className={`${styles.button} ${styles.rightAlign}`} onClick={handleNavigate} disabled={!asin}>
                            <span>{row.getCellData('inStockQty')}</span>
                        </button>
                    );
                }
            },
            {
                title: 'Revenue',
                id: 'revenue',
                dataKey: 'revenue',
                width: '7.5rem',
                sortable: true,
                renderRowCell: (row) => {
                    const handleNavigate = createNavigationHandler(row);
                    const asin = row.getCellData('asin');
                    return (
                        <button className={`${styles.button} ${styles.rightAlign}`} onClick={handleNavigate} disabled={!asin}>
                            <span>{formatValue(row.getCellData('revenue'), 'currency')}</span>
                        </button>
                    );
                }
            },
            {
                title: 'Profit',
                id: 'profit',
                dataKey: 'profit',
                width: '7.5rem',
                sortable: true,
                renderRowCell: (row) => {
                     const handleNavigate = createNavigationHandler(row);
                     const asin = row.getCellData('asin');
                     return (
                         <button className={`${styles.button} ${styles.rightAlign}`} onClick={handleNavigate} disabled={!asin}>
                            <span>{formatValue(row.getCellData('profit'), 'currency')}</span>
                        </button>
                     );
                }
            },
            {
                title: 'Units Sold',
                id: 'unitsSold',
                dataKey: 'unitsSold',
                width: '7.5rem',
                sortable: true,
                renderRowCell: (row) => {
                     const handleNavigate = createNavigationHandler(row);
                     const asin = row.getCellData('asin');
                     return (
                         <button className={`${styles.button} ${styles.rightAlign}`} onClick={handleNavigate} disabled={!asin}>
                            <span>{row.getCellData('unitsSold')}</span>
                        </button>
                     );
                }
            },
            {
                title: 'Refunds',
                id: 'refunds',
                dataKey: 'refunds',
                width: '7.5rem',
                sortable: true,
                renderRowCell: (row) => {
                    const handleNavigate = createNavigationHandler(row);
                    const asin = row.getCellData('asin');
                    return (
                        <button className={`${styles.button} ${styles.rightAlign}`} onClick={handleNavigate} disabled={!asin}>
                            <span>{row.getCellData('refunds')}</span>
                        </button>
                    );
                }
            }
        ];
    }, [navigate]);


    return (
        <PageContainer>
            <div className={styles.container}>
                <div className={styles.header}>
                    <h1 className={styles.title}>ASIN Profitability</h1>
                    <DateRangeButtons
                        timeFrame={timeFrame}
                        setTimeFrame={setTimeFrame}
                    />
                </div>

                <div className={styles.cards}>
                    {cardAnalytics.map((card, index) => {
                        return (
                                <div className={styles.height}>
                                    {card.image ? (
                                        <Card
                                            label={card.label}
                                            productName={card.name}
                                            value={card.value}
                                            isLoading={isOverviewLoading}
                                            href={card.href}
                                            imageUrl={card.image}
                                        />
                                     ) : (
                                        <TotalCard
                                            label={card.label}
                                            value={card.value}
                                            isLoading={isOverviewLoading}
                                            subHeading={card.subHeading}
                                        />
                                    )}
                                </div>
                        );
                    })}
                </div>

                <div className={styles.tableContainer}>
                    <Table
                        tableName={'profit-by-asin-table'}
                        showSort
                        data={filteredAndPaginatedData?.items || []}
                        tableContainerRef={tableContainerRef}
                        columns={columns}
                        isLoading={isProfitabilityLoading}
                        skeletonRows={limit}
                        sortDirection={sortDirection}
                        sortedColumnId={sortedColumnId}
                        header={
                            <TextInput
                                className={styles.searchInput}
                                placeholder={"Product Name, or ASIN"}
                                prefix={<IconSearch />}
                                value={search}
                                onChange={(value) => setSearch(value)}
                            />
                        }
                        footer={
                            <Pagination
                                total={filteredAndPaginatedData?.total || 0}
                                pageSize={filteredAndPaginatedData?.limit || limit}
                                onChangePageSize={(value) => setLimit(value)}
                                page={filteredAndPaginatedData?.page || 1}
                                onChangePage={(newPage) => setPage(newPage)}
                                showPageSelector={false}
                                pageSizeOptions={[5, 10, 20]}
                            />
                        }
                        onSort={setSort}
                    />
                </div>
            </div>
        </PageContainer>
    );
}

export default ProfitByAsinPage;